import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import useIsMobile from '../hooks/useIsMobile';
import { Button, Field, Input, Select, Icon, Logo, Toast } from '../ui';
import ThemeToggle from '../ui/ThemeToggle';
import { UB_DISTRICTS } from '../data/mongoliaDistricts';

type Tab = 'register' | 'login';
type View = Tab | 'forgot';
const MN: Record<string, string> = { 'Invalid credentials': 'Имэйл эсвэл нууц үг буруу байна', 'Account is disabled': 'Бүртгэл идэвхгүй байна' };
const errText = (e: any, fb: string) => { try { const d = JSON.parse(e.body).detail; return (typeof d === 'string' && (MN[d] || d)) || fb; } catch { return e?.message || fb; } };
const EMPTY6 = () => Array(6).fill('');

/** /register + /login — 2 таб. Бүртгэл: мэдээлэл → имэйлийн код → нууц үг тохируулах.
 *  Нэвтрэлт: имэйл + нууц үг. Нууц үг мартсан: имэйл → код → шинэ нууц үг. */
export default function AuthPage({ initialTab }: { initialTab: Tab }) {
  const navigate = useNavigate(); const loc = useLocation(); const m = useIsMobile(768);
  const { login, applyLogin } = useAuth();
  const [view, setView] = useState<View>(initialTab);
  useEffect(() => { setView(loc.pathname === '/login' ? 'login' : 'register'); }, [loc.pathname]);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState({ org_name: '', email: '', phone: '', district: '' });
  const [pw, setPw] = useState(''); const [pw2, setPw2] = useState(''); const [showPw, setShowPw] = useState(false);
  const [code, setCode] = useState<string[]>(EMPTY6());
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const [demoCode, setDemoCode] = useState(''); const [resendIn, setResendIn] = useState(0);
  const [toast, setToast] = useState('');
  useEffect(() => { if (resendIn <= 0) return; const t = setInterval(() => setResendIn(v => v - 1), 1000); return () => clearInterval(t); }, [resendIn]);

  const reset = (v: View) => { setView(v); setStep(1); setErr(''); setCode(EMPTY6()); setPw(''); setPw2(''); setDemoCode(''); };
  const switchTab = (t: Tab) => { reset(t); navigate(t === 'login' ? '/login' : '/register', { replace: true }); };
  const pwProblem = pw.length < 8 ? 'Нууц үг хамгийн багадаа 8 тэмдэгт' : pw !== pw2 ? 'Нууц үг тохирохгүй байна' : '';

  /* ── Бүртгэл ── */
  const regRequest = async () => {
    setBusy(true); setErr(''); setDemoCode('');
    try { const r = await api.post('/register/request-code', form); if (r.demo_code) setDemoCode(r.demo_code); setStep(2); setResendIn(30); setCode(EMPTY6()); }
    catch (e: any) { setErr(errText(e, 'Код илгээхэд алдаа гарлаа')); } finally { setBusy(false); }
  };
  const regCheck = async (full?: string) => {
    const c = full ?? code.join(''); if (c.length < 6) return;
    setBusy(true); setErr('');
    try { await api.post('/register/check-code', { email: form.email, code: c }); setStep(3); }
    catch (e: any) { setErr(errText(e, 'Код буруу байна')); setCode(EMPTY6()); } finally { setBusy(false); }
  };
  const regFinish = async () => {
    if (pwProblem) { setErr(pwProblem); return; }
    setBusy(true); setErr('');
    try {
      const res = await api.post('/register/verify-code', { email: form.email, code: code.join(''), password: pw });
      applyLogin(res, form.email); setToast('Тавтай морил! СӨХ бүртгэгдлээ.');
      setTimeout(() => navigate('/units', { replace: true }), 600);
    } catch (e: any) { setErr(errText(e, 'Бүртгэл амжилтгүй')); } finally { setBusy(false); }
  };

  /* ── Нэвтрэх ── */
  const doLogin = async () => {
    setBusy(true); setErr('');
    try { await login(form.email.trim(), pw); navigate('/dashboard', { replace: true }); }
    catch (e: any) { setErr(errText(e, 'Имэйл эсвэл нууц үг буруу')); } finally { setBusy(false); }
  };

  /* ── Нууц үг мартсан ── */
  const forgotRequest = async () => {
    setBusy(true); setErr(''); setDemoCode('');
    try { const r = await api.post('/auth/admin/forgot-password/request', { email: form.email }); if (r.demo_code) setDemoCode(r.demo_code); setStep(2); setResendIn(30); setCode(EMPTY6()); }
    catch (e: any) { setErr(errText(e, 'Код илгээхэд алдаа гарлаа')); } finally { setBusy(false); }
  };
  const forgotReset = async () => {
    if (pwProblem) { setErr(pwProblem); return; }
    const c = code.join(''); if (c.length < 6) { setErr('6 оронтой кодоо оруулна уу'); return; }
    setBusy(true); setErr('');
    try {
      await api.post('/auth/admin/forgot-password/reset', { email: form.email, code: c, new_password: pw });
      await login(form.email.trim(), pw); setToast('Нууц үг шинэчлэгдлээ. Тавтай морил!');
      setTimeout(() => navigate('/dashboard', { replace: true }), 600);
    } catch (e: any) { setErr(errText(e, 'Шинэчлэхэд алдаа гарлаа')); } finally { setBusy(false); }
  };

  const ErrLine = err ? <div className="sh-error" role="alert"><Icon name="alert" size={14} />{err}</div> : null;
  const PwFields = (label: string) => (
    <>
      <Field label={label} id="pw1" help="Хамгийн багадаа 8 тэмдэгт">
        <div style={{ position: 'relative' }}>
          <Input id="pw1" type={showPw ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)} placeholder="••••••••" style={{ paddingRight: 44 }} autoComplete="new-password" />
          <button type="button" onClick={() => setShowPw(s => !s)} aria-label={showPw ? 'Нууц үг нуух' : 'Нууц үг харах'} style={{ position: 'absolute', right: 10, top: 10, background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 2 }}><Icon name="eye" size={20} /></button>
        </div>
      </Field>
      <Field label="Нууц үг давтах" id="pw2" error={pw2 && pw !== pw2 ? 'Нууц үг тохирохгүй байна' : undefined}>
        <Input id="pw2" type={showPw ? 'text' : 'password'} value={pw2} onChange={e => setPw2(e.target.value)} placeholder="••••••••" autoComplete="new-password" invalid={!!pw2 && pw !== pw2} />
      </Field>
    </>
  );
  const CodeBlock = (onComplete: (full: string) => void, resend: () => void) => (
    <>
      <p style={{ margin: 0, color: 'var(--muted)', fontSize: 14 }}><b style={{ color: 'var(--ink)' }}>{form.email}</b> хаягт 6 оронтой код илгээв. <button onClick={() => { setStep(1); setErr(''); }} style={linkBtn}>Засах</button></p>
      {demoCode && <div className="sh-note sh-note--warn" style={{ fontSize: 13 }}><Icon name="info" size={16} />Туршилтын орчин — имэйл тохируулаагүй. Код: <b className="tnum" style={{ marginLeft: 4 }}>{demoCode}</b></div>}
      <OtpInput value={code} onChange={setCode} error={!!err} onComplete={onComplete} />
      <div style={{ fontSize: 13, color: 'var(--muted)' }}>Код ирсэнгүй юу? {resendIn > 0 ? <span>Дахин илгээх ({resendIn}с)</span> : <button onClick={resend} style={linkBtn}>Дахин илгээх</button>}</div>
    </>
  );

  const formEl = (
    <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
      <div className="sh-tabs" style={{ marginBottom: 22 }}>
        <button className={`sh-tab ${view === 'register' ? 'is-active' : ''}`} onClick={() => switchTab('register')}>СӨХ бүртгүүлэх</button>
        <button className={`sh-tab ${view !== 'register' ? 'is-active' : ''}`} onClick={() => switchTab('login')}>Нэвтрэх</button>
        <span style={{ flex: 1 }} />{m && <ThemeToggle size={40} style={{ alignSelf: 'center' }} />}
      </div>

      {/* ── БҮРТГЭЛ ── */}
      {view === 'register' && <Steps step={step} total={3} />}
      {view === 'register' && step === 1 && (
        <div style={{ display: 'grid', gap: 14 }}>
          <h1 style={h1}>СӨХ-ийн мэдээлэл</h1>
          <Field label="СӨХ-ийн нэр" id="org"><Input id="org" value={form.org_name} onChange={e => setForm({ ...form, org_name: e.target.value })} placeholder="Нарны Хороолол СӨХ" /></Field>
          <Field label="Имэйл" id="em" help="Баталгаажуулах код энэ хаягт ирнэ"><Input id="em" type="email" inputMode="email" autoComplete="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="tanii@mail.mn" /></Field>
          <Field label="Захирлын утас" id="ph"><Input id="ph" type="tel" inputMode="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="9911 2233" /></Field>
          <Field label="Дүүрэг" id="ds"><Select id="ds" value={form.district} onChange={e => setForm({ ...form, district: e.target.value })}><option value="">Сонгоно уу</option>{UB_DISTRICTS.map(d => <option key={d.name} value={d.name}>{d.name}</option>)}</Select></Field>
          {ErrLine}
          <Button style={{ minHeight: 48 }} block loading={busy} disabled={!form.org_name.trim() || !form.email.trim()} onClick={regRequest}>Баталгаажуулах код авах</Button>
          <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>Үргэлжлүүлснээр <a href="/privacy">үйлчилгээний нөхцөл</a>-ийг зөвшөөрнө.</div>
        </div>
      )}
      {view === 'register' && step === 2 && (
        <div style={{ display: 'grid', gap: 14 }}>
          <h1 style={h1}>Имэйлээ баталгаажуулна уу</h1>
          {CodeBlock(regCheck, regRequest)}{ErrLine}
          <Button style={{ minHeight: 48 }} block loading={busy} disabled={code.join('').length < 6} onClick={() => regCheck()}>Үргэлжлүүлэх</Button>
        </div>
      )}
      {view === 'register' && step === 3 && (
        <div style={{ display: 'grid', gap: 14 }}>
          <h1 style={h1}>Нууц үг тохируулах</h1>
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: 14 }}>Имэйл баталгаажлаа ✓. Цаашид <b style={{ color: 'var(--ink)' }}>{form.email}</b> + энэ нууц үгээр нэвтэрнэ.</p>
          {PwFields('Нууц үг')}{ErrLine}
          <Button style={{ minHeight: 48 }} block loading={busy} disabled={!!pwProblem} onClick={regFinish}>Бүртгэлийг дуусгаж эхлэх</Button>
        </div>
      )}

      {/* ── НЭВТРЭХ ── */}
      {view === 'login' && (
        <form style={{ display: 'grid', gap: 14 }} onSubmit={e => { e.preventDefault(); if (form.email && pw) doLogin(); }}>
          <h1 style={h1}>Тавтай морил</h1>
          <Field label="Имэйл" id="lem"><Input id="lem" type="email" inputMode="email" autoComplete="username" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="tanii@mail.mn" /></Field>
          <Field label="Нууц үг" id="lpw">
            <div style={{ position: 'relative' }}>
              <Input id="lpw" type={showPw ? 'text' : 'password'} autoComplete="current-password" value={pw} onChange={e => setPw(e.target.value)} placeholder="••••••••" style={{ paddingRight: 44 }} />
              <button type="button" onClick={() => setShowPw(s => !s)} aria-label={showPw ? 'Нууц үг нуух' : 'Нууц үг харах'} style={{ position: 'absolute', right: 10, top: 10, background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 2 }}><Icon name="eye" size={20} /></button>
            </div>
          </Field>
          <div style={{ textAlign: 'right', marginTop: -6 }}><button type="button" onClick={() => reset('forgot')} style={linkBtn}>Нууц үг мартсан?</button></div>
          {ErrLine}
          <Button type="submit" style={{ minHeight: 48 }} block loading={busy} disabled={!form.email.trim() || !pw}>Нэвтрэх</Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--muted)', fontSize: 12 }}><span style={{ flex: 1, height: 1, background: 'var(--line)' }} />эсвэл<span style={{ flex: 1, height: 1, background: 'var(--line)' }} /></div>
          <Button type="button" variant="secondary" style={{ minHeight: 44, borderColor: 'var(--line)', color: 'var(--ink)' }} block icon="eye" onClick={() => navigate('/u/demo')}>Демо байдлаар үзэх</Button>
          <div style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center' }}>Шинэ СӨХ үү? <button type="button" onClick={() => switchTab('register')} style={linkBtn}>Бүртгүүлэх</button></div>
        </form>
      )}

      {/* ── НУУЦ ҮГ МАРТСАН ── */}
      {view === 'forgot' && (
        <div style={{ display: 'grid', gap: 14 }}>
          <button onClick={() => reset('login')} className="sh-btn sh-btn--ghost" style={{ minHeight: 36, padding: '0 6px', justifySelf: 'start', color: 'var(--green)', fontWeight: 600 }}><Icon name="arrow-left" size={16} />Нэвтрэх</button>
          <h1 style={h1}>Нууц үг сэргээх</h1>
          {step === 1 ? (
            <>
              <p style={{ margin: 0, color: 'var(--muted)', fontSize: 14 }}>Бүртгэлтэй имэйлээ оруулна уу — баталгаажуулах код илгээнэ.</p>
              <Field label="Имэйл" id="fem"><Input id="fem" type="email" inputMode="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="tanii@mail.mn" onKeyDown={e => { if (e.key === 'Enter' && form.email) forgotRequest(); }} /></Field>
              {ErrLine}
              <Button style={{ minHeight: 48 }} block loading={busy} disabled={!form.email.trim()} onClick={forgotRequest}>Код авах</Button>
            </>
          ) : (
            <>
              {CodeBlock(() => {}, forgotRequest)}
              {PwFields('Шинэ нууц үг')}{ErrLine}
              <Button style={{ minHeight: 48 }} block loading={busy} disabled={code.join('').length < 6 || !!pwProblem} onClick={forgotReset}>Нууц үг шинэчлэх</Button>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr 1fr' }}>
      {!m && (
        <aside style={{ background: 'linear-gradient(160deg,#2f7d52,#236844)', color: '#fff', padding: 48, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Logo size={40} /><span style={{ fontWeight: 700, fontSize: 18 }}>Сайн Хөрш</span><span style={{ flex: 1 }} /><ThemeToggle size={40} style={{ background: 'rgba(255,255,255,.12)', borderColor: 'rgba(255,255,255,.25)', color: '#fff' }} /></div>
          <div>
            <h2 style={{ fontSize: 34, fontWeight: 700, lineHeight: 1.15, margin: '0 0 22px', letterSpacing: '-.02em' }}>Айл бүрд QR.<br />СӨХ-д цэгцтэй санхүү.</h2>
            {['Оршин суугч апп татахгүй — QR уншуулаад л төлнө', 'Төлбөр автоматаар бүртгэгдэж, өртэй айл харагдана', 'Санал хүсэлт тоотоор нь ирнэ'].map(t => (
              <div key={t} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12, fontSize: 15 }}><Icon name="check-circle" size={20} style={{ color: 'var(--gold)', flexShrink: 0, marginTop: 2 }} />{t}</div>
            ))}
          </div>
          <div style={{ fontSize: 12, opacity: .75, lineHeight: 1.8 }}><div>© 2026 Сайн Хөрш · sainhursh.mn</div><div>Харилцах утас: <a href="tel:96900520" style={{ color: 'inherit', fontWeight: 600, textDecoration: 'none' }}>96900520</a></div></div>
        </aside>
      )}
      <main style={{ padding: m ? '28px 20px' : 48, display: 'flex', flexDirection: 'column', justifyContent: m ? 'flex-start' : 'center' }}>
        {m && <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 }}><Logo size={32} /><span style={{ fontWeight: 700 }}>Сайн Хөрш</span></div>}
        {formEl}
        <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--muted)', padding: '18px 16px 8px', lineHeight: 1.8 }}><div>© 2026 Сайн Хөрш · sainhursh.mn</div><div>Харилцах утас: <a href="tel:96900520" style={{ color: 'inherit', fontWeight: 600, textDecoration: 'none' }}>96900520</a></div></div>
      </main>
      {toast && <Toast message={toast} onClose={() => setToast('')} />}
    </div>
  );
}

const h1: React.CSSProperties = { fontSize: 24, fontWeight: 700, margin: 0 };
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--green)', fontWeight: 600, cursor: 'pointer', padding: 0, font: 'inherit' };

function Steps({ step, total }: { step: number; total: number }) {
  const dot = (n: number) => { const done = step > n, active = step === n; return <span key={n} style={{ width: 22, height: 22, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: done || active ? 'var(--green)' : 'transparent', color: done || active ? '#fff' : 'var(--muted)', border: done || active ? 'none' : '1.5px solid var(--line)' }}>{done ? <Icon name="check" size={13} /> : n}</span>; };
  const items: React.ReactNode[] = [];
  for (let n = 1; n <= total; n++) { items.push(dot(n)); if (n < total) items.push(<span key={`l${n}`} style={{ width: 28, height: 1, background: step > n ? 'var(--green)' : 'var(--line)' }} />); }
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>{items}<span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 6 }}>Алхам {step}/{total}</span></div>;
}

/** 6 нүдтэй OTP: авто-focus, paste, numeric, one-time-code. */
function OtpInput({ value, onChange, error, onComplete }: { value: string[]; onChange: (v: string[]) => void; error?: boolean; onComplete?: (full: string) => void }) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => { refs.current[0]?.focus(); }, []);
  const set = (i: number, ch: string) => { const v = [...value]; v[i] = ch; onChange(v); if (ch && i < 5) refs.current[i + 1]?.focus(); if (v.every(Boolean) && v.join('').length === 6) setTimeout(() => onComplete?.(v.join('')), 50); };
  const onPaste = (e: React.ClipboardEvent) => { const d = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6); if (d.length) { e.preventDefault(); const v = Array(6).fill(''); d.split('').forEach((c, i) => v[i] = c); onChange(v); refs.current[Math.min(d.length, 5)]?.focus(); if (d.length === 6) setTimeout(() => onComplete?.(d), 50); } };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }} onPaste={onPaste}>
      {value.map((ch, i) => (
        <input key={i} ref={el => { refs.current[i] = el; }} value={ch} inputMode="numeric" autoComplete={i === 0 ? 'one-time-code' : 'off'} maxLength={1} aria-label={`Кодын ${i + 1}-р цифр`}
          onChange={e => set(i, e.target.value.replace(/\D/g, '').slice(-1))} onKeyDown={e => { if (e.key === 'Backspace' && !ch && i > 0) refs.current[i - 1]?.focus(); }} className="tnum"
          style={{ height: 56, textAlign: 'center', fontSize: 24, fontWeight: 700, borderRadius: 'var(--r-md)', background: 'var(--card)', color: 'var(--ink)', border: `1.5px solid ${error ? 'var(--red)' : ch ? 'var(--green)' : 'var(--line)'}`, width: '100%', padding: 0 }} />
      ))}
    </div>
  );
}
